import { useSyncExternalStore } from "react";

// The breadcrumb row of a page (GoldCrumbs) has a small "slot" on its right. A page wraps its
// Sync Live / Refresh / Time-filter row in <PageToolbar> (components/shared/PageToolbar.jsx) and
// that row is drawn in the slot — in the first row, beside the breadcrumb, smaller — instead of
// in its own row below. The last slot mounted is the active one.
const createSlot = () => {
  let stack = [];
  let active = null;
  const listeners = new Set();
  const emit = () => { active = stack[stack.length - 1] || null; listeners.forEach((l) => l()); };
  return {
    subscribe: (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    get: () => active,
    register: (el) => { if (el && !stack.includes(el)) { stack.push(el); emit(); } },
    unregister: (el) => { const n = stack.length; stack = stack.filter((e) => e !== el); if (stack.length !== n) emit(); },
  };
};

export const pageToolbarSlot = createSlot();
export const usePageToolbarSlot = () => useSyncExternalStore(pageToolbarSlot.subscribe, pageToolbarSlot.get);

// A second slot, right after the breadcrumb itself (Home › Section › Page › [here]): the Financials pages draw
// their Company ▾ › Store ▾ switchers there, in the first row, instead of in a row of their own (PageCrumbTail).
export const pageCrumbTailSlot = createSlot();
export const usePageCrumbTailSlot = () => useSyncExternalStore(pageCrumbTailSlot.subscribe, pageCrumbTailSlot.get);

// A third slot: the RIGHT end of the sub-tab row under the first row (Products page: the All / Digital / Physical row).
// A tab's filter bar is drawn there (PageSubRight) — small, under the chips + Refresh and right-aligned — instead of
// in a card of its own further down the page.
export const pageSubRightSlot = createSlot();
export const usePageSubRightSlot = () => useSyncExternalStore(pageSubRightSlot.subscribe, pageSubRightSlot.get);

// A slot at the RIGHT end of the sub-tab row itself (row 2) — for a short filter bar that fits beside the sub-tabs
// (Pricing: All Stores / All Status / All Schedules / Refresh): <PageSubRight inline>.
export const pageSubRowEndSlot = createSlot();
export const usePageSubRowEndSlot = () => useSyncExternalStore(pageSubRowEndSlot.subscribe, pageSubRowEndSlot.get);

// A fourth slot: the LEFT end of the third row (under the sub-tab row) — a tab's summary chips (PageStats) go there,
// the tab's filter bar goes to the right of the same row (PageSubRight).
export const pageStatsSlot = createSlot();
export const usePageStatsSlot = () => useSyncExternalStore(pageStatsSlot.subscribe, pageStatsSlot.get);

// The breadcrumb's Home icon: while a page registers a handler (usePageHome) the icon is clickable.
let homeHandler = null;
const homeListeners = new Set();
export const pageHome = {
  subscribe: (cb) => { homeListeners.add(cb); return () => homeListeners.delete(cb); },
  get: () => homeHandler,
  set: (fn) => { if (homeHandler !== fn) { homeHandler = fn; homeListeners.forEach((l) => l()); } },
};
export const usePageHomeHandler = () => useSyncExternalStore(pageHome.subscribe, pageHome.get);
